/**
 * ORCHESTRATOR DOMAIN MODEL — Phase D foundation.
 *
 * These types describe the conceptual architecture:
 *   User → Ugarits Orchestrator → Provider Adapter → Selected Model
 * Claude/OpenAI/etc. are interchangeable PROVIDERS behind the
 * orchestrator, never the product's identity — nothing in the UI should
 * ever read "User → Claude" or "User → OpenAI".
 *
 * This file is additive: it does NOT change the existing `Agent`/
 * `Approval` entities in entities.ts (those already back real, working
 * CRUD + the Approval Center and must not be broken). It gives the new
 * AI Command / AI Operations modules a typed vocabulary to build on.
 * Deliberately kept small — this is a foundation, not a finished
 * orchestration engine.
 */

/** The 10 functional departments Phase D's AI Operations map is organized around. */
export type AgentDepartment =
  | 'executive' | 'operations' | 'creator_manager' | 'content' | 'sales'
  | 'growth' | 'finance' | 'analytics' | 'developer' | 'support';

/**
 * Conceptual permission ladder (section 8 of the Phase D brief) — distinct
 * from the existing `Agent.permissionLevel` (1|2|3) already wired into the
 * live Approval Center/backend contract, which is left untouched. This is
 * the descriptive model shown in AI Operations/Agent detail; a future
 * phase can reconcile the two once a real approval-routing engine exists.
 */
export type AgentPermissionLevel = 0 | 1 | 2 | 3;

export interface AgentPermissionMeta {
  level: AgentPermissionLevel;
  labelKey: string;
  descriptionKey: string;
}

export const AGENT_PERMISSION_LEVELS: AgentPermissionMeta[] = [
  { level: 0, labelKey: 'permissionConcept.level0.label', descriptionKey: 'permissionConcept.level0.desc' },
  { level: 1, labelKey: 'permissionConcept.level1.label', descriptionKey: 'permissionConcept.level1.desc' },
  { level: 2, labelKey: 'permissionConcept.level2.label', descriptionKey: 'permissionConcept.level2.desc' },
  { level: 3, labelKey: 'permissionConcept.level3.label', descriptionKey: 'permissionConcept.level3.desc' },
];

/** High-risk action categories that must never become autonomous, regardless of permission level (section 8). */
export const HIGH_RISK_ACTION_CATEGORIES = [
  'financial', 'contract_execution', 'publishing', 'record_deletion',
  'permission_changes', 'sensitive_communication', 'ad_spend', 'security_connector_changes',
] as const;
export type HighRiskActionCategory = (typeof HIGH_RISK_ACTION_CATEGORIES)[number];

/** Operational status shown on an AI Operations agent node. Distinct from
 * (but mapped from) `STATUS_REGISTRY.agent` — see mapAgentOperationalStatus(). */
export type AgentOperationalStatus =
  | 'configured_preview' | 'idle' | 'active' | 'working' | 'waiting_approval'
  | 'waiting_connector' | 'paused' | 'error';

export interface ProviderReference {
  key: 'anthropic' | 'openai' | 'future_provider' | 'custom';
  displayName: string;
  configured: boolean; // true only once real backend credentials exist — never faked
}

export interface ModelReference {
  provider: ProviderReference['key'];
  model: string;
  purposeKey: string; // e.g. 'aiModels.purpose.executiveReasoning'
  status: 'configured' | 'not_connected' | 'unavailable' | 'disabled';
}

export type AgentTaskStatus = 'draft' | 'queued' | 'running' | 'waiting_approval' | 'completed' | 'failed' | 'cancelled';

/** Typed shape for a future real task queue. No task execution engine
 * exists yet in Preview — this type is used only to describe "current
 * task: none" / "queue: 0" honestly, never to fabricate activity. */
export interface AgentTask {
  id: string;
  title: string;
  description?: string;
  requestedBy: string;
  assignedAgent: AgentDepartment;
  status: AgentTaskStatus;
  priority: 'low' | 'medium' | 'high';
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  requiredSkills: string[];
  requiredTools: string[];
  approvalRequirement?: string;
  result?: string;
  error?: string;
}

export type MemoryDomain =
  | 'company' | 'brand' | 'services' | 'sop' | 'creators' | 'clients' | 'leads'
  | 'partnerships' | 'contracts' | 'projects' | 'content' | 'decisions' | 'knowledge' | 'agent_learnings';

export type SkillStatus = 'available_locally' | 'requires_connector' | 'requires_ai_provider' | 'disabled';

export interface SkillDefinition {
  id: string;
  nameKey: string;
  descriptionKey: string;
  category: string;
  inputDescriptionKey: string;
  expectedOutputKey: string;
  requiredTools: string[];
  requiredConnectors: string[];
  approvalRequired: boolean;
  compatibleDepartments: AgentDepartment[];
  status: SkillStatus;
}

/** A department's static definition — role, default permission concept,
 * skills, tools, and memory domains it's scoped to. This is configuration
 * (like modules.config.ts), not a live agent record; the AI Operations
 * map joins this against the real `Agent[]` from dataService to render
 * actual configured/status/provider/model per agent. */
export interface AgentDepartmentDefinition {
  department: AgentDepartment;
  nameKey: string;
  taglineKey: string;
  defaultPermission: AgentPermissionLevel;
  defaultSkills: string[]; // SkillDefinition ids
  memoryDomains: MemoryDomain[];
  icon: string;
}

/** Local Command Router result shape — see services/commandRouter.ts. */
export type LocalCommandResultKind = 'local_preview' | 'ai_generated' | 'ai_error' | 'not_connected' | 'unknown';

export interface LocalCommandRoutingStep {
  labelKey: string;
}

export interface LocalCommandResultRow {
  label: string;
  value: string;
  route?: string;
}

export interface LocalCommandResult {
  kind: LocalCommandResultKind;
  commandId?: string;
  titleKey?: string;
  text?: string;
  provider?: string;
  model?: string;
  error?: string;
  rows?: LocalCommandResultRow[];
  emptyKey?: string;
  route?: string;
  routing: LocalCommandRoutingStep[];
}
